import type { IDailyHealthRecord } from '@/hooks/useAppState';

interface HealthFourRingsProps {
  record: IDailyHealthRecord | null;
  size?: number;
  strokeWidth?: number;
  gap?: number;
}

/**
 * 健康打卡四环进度环
 * 四个环分别代表：静息心率（蓝）、睡眠（浅蓝）、体重（浅灰）、心情（黄橙）
 * 每填一项，对应环的进度变成完整圆环
 */
export default function HealthFourRings({ record, size = 100, strokeWidth = 6, gap = 4 }: HealthFourRingsProps) {
  const items = [
    { key: 'restingHeartRate', filled: record?.restingHeartRate !== undefined, color: 'var(--primary)' },
    { key: 'sleepHours', filled: record?.sleepHours !== undefined, color: 'var(--info)' },
    { key: 'weight', filled: record?.weight !== undefined, color: 'var(--muted-foreground)' },
    { key: 'mood', filled: record?.mood !== undefined, color: 'var(--warning)' },
  ];

  const center = size / 2;
  const maxRadius = center - strokeWidth / 2 - 2;
  const ringStep = (strokeWidth + gap);

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        {items.map((item, index) => {
          const radius = maxRadius - index * ringStep;
          const circumference = 2 * Math.PI * radius;
          const progress = item.filled ? 1 : 0;
          const dashOffset = circumference * (1 - progress);

          return (
            <g key={item.key}>
              {/* 背景轨道 */}
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke="var(--border)"
                strokeWidth={strokeWidth}
                strokeOpacity={0.35}
              />
              {/* 进度环 */}
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={item.color}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                style={{ transition: 'stroke-dashoffset 0.5s ease' }}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
}
